import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { cn } from "../lib/utils";

export function MultiSelectDropdown({
  label,
  options,
  selectedValues,
  onChange,
  allLabel = "Todas",
  confirmSelection = false,
  applyLabel = "FILTRAR",
  className,
  contentClassName,
}) {
  const [open, setOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(selectedValues);
  const activeValues = confirmSelection ? pendingValues : selectedValues;
  const selectedSet = new Set(activeValues);
  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label);

  const triggerLabel = selectedLabels.length === 0
    ? allLabel
    : selectedLabels.length === 1
      ? selectedLabels[0]
      : `${selectedLabels.length} selecionadas`;

  useEffect(() => {
    if (!open) {
      setPendingValues(selectedValues);
    }
  }, [open, selectedValues]);

  const toggleValue = (value) => {
    const next = selectedSet.has(value)
      ? activeValues.filter((item) => item !== value)
      : [...activeValues, value];

    if (confirmSelection) {
      setPendingValues(next);
    } else {
      onChange(next);
    }
  };

  const applySelection = () => {
    onChange(pendingValues);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn("h-10 justify-between gap-3 px-3", className)}
        >
          <span className="min-w-0 truncate text-left">
            <span className="text-muted-foreground">{label}: </span>
            <span className="font-medium">{triggerLabel}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={cn("w-56 p-0", contentClassName)}>
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-48 overflow-y-auto p-1">
          {options.length > 0 ? (
            options.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={selectedSet.has(option.value)}
                onCheckedChange={() => toggleValue(option.value)}
                onSelect={(event) => event.preventDefault()}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              Nenhuma opção
            </div>
          )}
        </div>
        {confirmSelection && (
          <div className="border-t p-2">
            <Button
              type="button"
              className="btn-glow h-9 w-full bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={applySelection}
            >
              {applyLabel}
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
